import { useEffect, useState } from "react";
import { CalendarClock, Play, ShieldAlert, ShieldCheck, RefreshCw, ScrollText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const STATUS = { ok: "bg-emerald-100 text-emerald-800", partial: "bg-amber-100 text-amber-800", error: "bg-red-100 text-red-800", timeout: "bg-red-100 text-red-800",
  skipped: "bg-slate-100 text-slate-600", disarmed: "bg-amber-50 text-amber-700", baseline: "bg-slate-100 text-slate-600" };

function StatusBadge({ s }) {
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS[s] || "bg-slate-100 text-slate-600"}`} data-testid={`cron-status-${s || "none"}`}>{s || "mai eseguito"}</span>;
}

function ArmDialog({ open, onClose, onDone, armed }) {
  const [confirm, setConfirm] = useState("");
  const submit = async () => {
    try { await api.post("/superadmin/cron/settings", { armed: !armed, confirm }); toast.success(armed ? "Modalità sicurezza riattivata" : "Job operativi attivati"); onDone(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cron-arm-dialog">
        <DialogHeader><DialogTitle>{armed ? "Torna in modalità sicurezza" : "Attiva i job operativi"}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{armed ? "I job con effetti sui dati (invii, reinvii, digest, retention) torneranno a essere solo registrati senza eseguire nulla."
          : "In modalità sicurezza girano solo i job di monitoraggio. Attivando, i job operativi partiranno dalla PROSSIMA scadenza: nessun recupero retroattivo. Scrivi ATTIVA per confermare."}</p>
        {!armed && <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="ATTIVA" data-testid="cron-arm-confirm" />}
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button><Button onClick={submit} data-testid="cron-arm-submit">{armed ? "Riattiva sicurezza" : "Attiva"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunDialog({ job, open, onClose, onDone }) {
  const [confirm, setConfirm] = useState("");
  useEffect(() => setConfirm(""), [job]);
  const submit = async () => {
    try { const r = await api.post(`/superadmin/cron/run/${job.job}`, { confirm }); toast.success(`Job ${job.job}: ${r.data.run.status}`); onDone(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!job) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cron-run-dialog">
        <DialogHeader><DialogTitle>Esegui ora: {job.job}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{job.desc}</p>
        {!job.safe && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">Job con effetti sui dati: richiede la modalità operativa attiva e la conferma esplicita. Scrivi <b>{job.job}</b>.</p>}
        {!job.safe && <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={job.job} data-testid="cron-run-confirm" />}
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button><Button onClick={submit} data-testid="cron-run-submit">Esegui</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CronPanel() {
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [logJob, setLogJob] = useState("");
  const [arm, setArm] = useState(false);
  const [runJob, setRunJob] = useState(null);
  const load = async () => {
    const r = await api.get("/superadmin/cron/jobs"); setData(r.data);
    const l = await api.get("/superadmin/cron/runs", { params: { job: logJob || undefined, limit: 30 } }); setRuns(l.data.items);
  };
  useEffect(() => { load().catch(() => {}); }, [logJob]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (job, enabled) => {
    try { await api.post("/superadmin/cron/settings", { jobs: { [job]: { enabled } } }); toast.success(`${job}: ${enabled ? "abilitato" : "disabilitato"}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!data) return <p className="text-sm text-slate-500">Caricamento…</p>;
  const armed = data.settings.armed;
  return (
    <div className="space-y-4" data-testid="cron-panel">
      <ArmDialog open={arm} onClose={() => setArm(false)} onDone={load} armed={armed} />
      <RunDialog job={runJob} open={!!runJob} onClose={() => setRunJob(null)} onDone={load} />
      <div className={`rounded-xl border p-4 flex flex-wrap items-center gap-3 ${armed ? "border-emerald-200 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="cron-mode-banner">
        {armed ? <ShieldCheck className="h-5 w-5 text-emerald-700" /> : <ShieldAlert className="h-5 w-5 text-amber-700" />}
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-semibold text-slate-900">{armed ? "Modalità operativa: tutti i job abilitati vengono eseguiti" : "Modalità sicurezza (primo avvio): eseguiti solo i job di monitoraggio"}</p>
          <p className="text-xs text-slate-600">5 corsie piattaforma (process-campaigns invariato + fast/hourly/monitor/nightly) · {data.jobs.length} job · nessun recupero retroattivo</p>
        </div>
        <Button size="sm" variant={armed ? "outline" : "default"} onClick={() => setArm(true)} data-testid="cron-arm-button">{armed ? "Torna in sicurezza" : "Attiva job operativi"}</Button>
        <Button size="sm" variant="ghost" onClick={load} data-testid="cron-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-xs sm:text-sm" data-testid="cron-jobs-table">
          <thead className="bg-slate-50 text-slate-600"><tr>
            <th className="text-left p-2">Job</th><th className="text-left p-2">Pianificazione</th><th className="text-left p-2">Ultima</th><th className="text-left p-2">Prossima</th><th className="text-left p-2">Stato</th><th className="text-left p-2">Err.</th><th className="text-left p-2">On</th><th className="p-2"></th>
          </tr></thead>
          <tbody>
            {data.jobs.map((j) => (
              <tr key={j.job} className="border-t border-slate-100" data-testid={`cron-job-${j.job}`}>
                <td className="p-2"><div className="font-medium text-slate-900 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5 text-slate-400" />{j.job}{j.safe && <span className="text-[10px] text-emerald-700 border border-emerald-200 rounded px-1">sicuro</span>}</div><div className="text-[11px] text-slate-500 max-w-xs">{j.desc}</div></td>
                <td className="p-2 font-mono text-[11px] text-slate-600">{j.cron}<div className="text-slate-400">{j.lanes.join(", ")}{j.in_app_loop ? " + loop in-app" : ""}</div></td>
                <td className="p-2 text-slate-600 whitespace-nowrap">{fmt(j.last_run_at)}</td>
                <td className="p-2 text-slate-600 whitespace-nowrap">{fmt(j.next_run)}</td>
                <td className="p-2"><StatusBadge s={j.last_status} /></td>
                <td className="p-2 text-slate-600">{j.consecutive_errors || 0}</td>
                <td className="p-2"><Switch checked={j.enabled} onCheckedChange={(v) => toggle(j.job, v)} data-testid={`cron-toggle-${j.job}`} /></td>
                <td className="p-2 whitespace-nowrap"><Button size="sm" variant="outline" onClick={() => setRunJob(j)} data-testid={`cron-run-${j.job}`}><Play className="h-3.5 w-3.5" /></Button> <Button size="sm" variant="ghost" onClick={() => setLogJob(j.job)} data-testid={`cron-log-${j.job}`}><ScrollText className="h-3.5 w-3.5" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="cron-runs">
        <div className="flex items-center justify-between"><p className="text-sm font-semibold text-slate-900">Storico esecuzioni {logJob && <span className="text-slate-500">· {logJob}</span>}</p>{logJob && <Button size="sm" variant="ghost" onClick={() => setLogJob("")} data-testid="cron-log-all">Tutti</Button>}</div>
        {runs.length === 0 && <p className="text-xs text-slate-500">Nessuna esecuzione registrata.</p>}
        {runs.map((r) => (
          <div key={r.run_id} className="text-xs border-t border-slate-100 pt-2 flex flex-wrap gap-x-3 gap-y-1" data-testid={`cron-run-row-${r.run_id}`}>
            <span className="font-medium text-slate-800">{r.job}</span><StatusBadge s={r.status} /><span className="text-slate-500">{fmt(r.started_at)}</span><span className="text-slate-500">{r.lane} · {r.trigger}{r.by ? ` · ${r.by}` : ""}</span>
            {r.duration_ms != null && <span className="text-slate-500">{r.duration_ms} ms</span>}{r.tenants_total != null && <span className="text-slate-500">{(r.tenants || []).filter((t) => t.status === "ok").length}/{r.tenants_total} tenant</span>}
            {(r.error || r.reason) && <span className="text-red-700">{r.error || r.reason}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
